'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ChatHeader from './ChatHeader';
import ChatMessageArea from './ChatMessageArea';
import MessageComposer from './MessageComposer';
import ContextMenu from './ContextMenu';
import ReactionPicker from './ReactionPicker';
import SelectionHeader from './SelectionHeader';
import ForwardShareModal from './ForwardShareModal';
import VideoCallScreen from './VideoCallScreen';
import VoiceCallScreen from './VoiceCallScreen';
import MediaGalleryScreen from './MediaGalleryScreen';
import PinnedMessageBanner from './PinnedMessageBanner';
import { Message, ContextMenuState, ReactionPickerState } from './types';
import { INITIAL_MESSAGES } from './mockData';
import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

// Build a stable conversation_id from two user UUIDs
function buildConversationId(a: string, b: string): string {
  return [a, b].sort().join('_');
}

export default function ChatScreen({ chatId, onBack, onViewProfile, onOpenSettings, localUserId, remoteUserId, remoteUserName }: { chatId?: string; onBack?: () => void; onViewProfile?: () => void; onOpenSettings?: () => void; localUserId?: string; remoteUserId?: string; remoteUserName?: string; }) {
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [reactionPicker, setReactionPicker] = useState<ReactionPickerState | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [forwardShareModal, setForwardShareModal] = useState<{ message: Message; mode: 'forward' | 'share' } | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [inVideoCall, setInVideoCall] = useState(false);
  const [inVoiceCall, setInVoiceCall] = useState(false);
  const [inGallery, setInGallery] = useState(false);
  const [pinnedMessage, setPinnedMessage] = useState<Message | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const messageRefs = useRef<Record<string, HTMLElement | null>>({});

  const conversationId = localUserId && remoteUserId
    ? buildConversationId(localUserId, remoteUserId)
    : chatId ?? 'default';

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Map DB row → Message
  function rowToMessage(row: Record<string, unknown>): Message {
    return {
      id: row.id as string,
      senderId: row.sender_id === localUserId ? 'me' : 'alex',
      text: (row.text as string | null) ?? undefined,
      imageUrl: (row.image_url as string | null) ?? undefined,
      imageAlt: (row.image_alt as string | null) ?? undefined,
      imageWidth: (row.image_width as number | null) ?? undefined,
      imageHeight: (row.image_height as number | null) ?? undefined,
      audioUrl: (row.audio_url as string | null) ?? undefined,
      audioDuration: (row.audio_duration as number | null) ?? undefined,
      fileUrl: (row.file_url as string | null) ?? undefined,
      fileName: (row.file_name as string | null) ?? undefined,
      fileSize: (row.file_size as number | null) ?? undefined,
      fileMime: (row.file_mime as string | null) ?? undefined,
      timestamp: row.created_at as string,
      editedAt: (row.edited_at as string | null) ?? undefined,
      status: (row.msg_status as Message['status']) ?? 'sent',
      reactions: [],
      type: (row.msg_type as Message['type']) ?? 'text',
    };
  }

  // Load messages from Supabase when real user IDs are available
  useEffect(() => {
    if (!localUserId || !remoteUserId) return;

    const loadMessages = async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (!error && data && data.length > 0) {
        setMessages(data.map(rowToMessage));

        // Mark unread messages from the other user as read
        const unreadIds = data
          .filter((m) => m.receiver_id === localUserId && m.msg_status !== 'read')
          .map((m) => m.id as string);

        if (unreadIds.length > 0) {
          await supabase
            .from('messages')
            .update({ msg_status: 'read' })
            .in('id', unreadIds);
        }
      }
    };

    loadMessages();

    // Real-time subscription for status updates and new messages
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newMsg = rowToMessage(payload.new as Record<string, unknown>);
            setMessages((prev) => {
              // Avoid duplicates (optimistic update already added it)
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });

            // Auto-mark as read if we are the receiver and chat is open
            if ((payload.new as Record<string, unknown>).receiver_id === localUserId) {
              await supabase
                .from('messages')
                .update({ msg_status: 'read' })
                .eq('id', (payload.new as Record<string, unknown>).id as string);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as Record<string, unknown>;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === updated.id
                  ? {
                      ...m,
                      status: (updated.msg_status as Message['status']) ?? m.status,
                      text: (updated.text as string | null) ?? m.text,
                      editedAt: (updated.edited_at as string | null) ?? m.editedAt,
                    }
                  : m
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setMessages((prev) =>
              prev.filter((m) => m.id !== (payload.old as Record<string, unknown>).id)
            );
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
    };
  }, [localUserId, remoteUserId, conversationId]);

  // Load pinned message for this conversation
  useEffect(() => {
    if (!localUserId || !remoteUserId) return;

    const loadPinned = async () => {
      const { data, error } = await supabase
        .from('pinned_messages')
        .select('message_id')
        .eq('conversation_id', conversationId)
        .order('pinned_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        const { data: msgData } = await supabase
          .from('messages')
          .select('*')
          .eq('id', data.message_id)
          .maybeSingle();

        if (msgData) {
          setPinnedMessage({ ...rowToMessage(msgData as Record<string, unknown>), isPinned: true });
        }
      }
    };

    loadPinned();
  }, [localUserId, remoteUserId, conversationId]);

  // Check if remote user is blocked
  useEffect(() => {
    if (!localUserId || !remoteUserId) return;
    const checkBlocked = async () => {
      const { data } = await supabase
        .from('blocked_contacts')
        .select('id')
        .eq('blocker_id', localUserId)
        .eq('blocked_id', remoteUserId)
        .maybeSingle();
      setIsBlocked(!!data);
    };
    checkBlocked();
  }, [localUserId, remoteUserId]);

  const handleBlockContact = useCallback(async () => {
    if (!localUserId || !remoteUserId) return;
    if (isBlocked) {
      // Unblock
      await supabase
        .from('blocked_contacts')
        .delete()
        .eq('blocker_id', localUserId)
        .eq('blocked_id', remoteUserId);
      setIsBlocked(false);
    } else {
      // Block
      await supabase
        .from('blocked_contacts')
        .insert({ blocker_id: localUserId, blocked_id: remoteUserId });
      setIsBlocked(true);
    }
  }, [localUserId, remoteUserId, isBlocked]);

  const handleDeleteChat = useCallback(async () => {
    if (!localUserId || !remoteUserId) return;
    // Delete all messages in this conversation that the local user sent
    await supabase
      .from('messages')
      .delete()
      .eq('conversation_id', conversationId)
      .eq('sender_id', localUserId);
    // Also delete messages received by local user in this conversation
    await supabase
      .from('messages')
      .delete()
      .eq('conversation_id', conversationId)
      .eq('receiver_id', localUserId);
    setMessages([]);
    onBack?.();
  }, [localUserId, remoteUserId, conversationId, onBack]);

  const handlePinMessage = useCallback(async (msgId: string) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;

    // If this message is already pinned, unpin it
    if (pinnedMessage?.id === msgId) {
      setPinnedMessage(null);
      if (localUserId) {
        await supabase
          .from('pinned_messages')
          .delete()
          .eq('conversation_id', conversationId)
          .eq('message_id', msgId);
      }
      return;
    }

    // Pin new message (replace any existing pin)
    setPinnedMessage({ ...msg, isPinned: true });

    if (localUserId) {
      // Remove existing pins for this conversation first
      await supabase
        .from('pinned_messages')
        .delete()
        .eq('conversation_id', conversationId);

      await supabase
        .from('pinned_messages')
        .insert({
          conversation_id: conversationId,
          message_id: msgId,
          pinned_by: localUserId,
        });
    }
  }, [messages, pinnedMessage, localUserId, conversationId]);

  const handleUnpinMessage = useCallback(async () => {
    if (!pinnedMessage) return;
    const msgId = pinnedMessage.id;
    setPinnedMessage(null);
    if (localUserId) {
      await supabase
        .from('pinned_messages')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('message_id', msgId);
    }
  }, [pinnedMessage, localUserId, conversationId]);

  const scrollToPinnedMessage = useCallback(() => {
    if (!pinnedMessage) return;
    const el = messageRefs.current[pinnedMessage.id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [pinnedMessage]);

  const sendMessage = useCallback(async (text: string) => {
    const tempId = `msg-${Date.now()}`;
    const now = new Date().toISOString();

    const optimisticMsg: Message = {
      id: tempId,
      senderId: 'me',
      text,
      timestamp: now,
      status: 'sending',
      replyTo: replyTo ?? undefined,
      reactions: [],
      type: 'text',
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setReplyTo(null);

    // If real user IDs available, persist to Supabase
    if (localUserId && remoteUserId) {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: localUserId,
          receiver_id: remoteUserId,
          text,
          msg_type: 'text',
          msg_status: 'sent',
        })
        .select()
        .single();

      if (!error && data) {
        // Replace optimistic message with real one
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { ...rowToMessage(data as Record<string, unknown>), reactions: [] } : m
          )
        );

        // Simulate delivered after 800ms (in real app, receiver's client updates this)
        setTimeout(async () => {
          await supabase
            .from('messages')
            .update({ msg_status: 'delivered' })
            .eq('id', (data as Record<string, unknown>).id as string);
        }, 800);
      } else {
        // Fallback: local-only status simulation
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, status: 'sent' as const } : m))
        );
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'delivered' as const } : m))
          );
        }, 800);
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'read' as const } : m))
          );
        }, 2000);
      }
    } else {
      // No real user IDs — local simulation only
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, status: 'delivered' as const } : m))
        );
      }, 800);
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, status: 'read' as const } : m))
        );
      }, 2000);
    }
  }, [replyTo, localUserId, remoteUserId, conversationId]);

  const sendVoiceMessage = useCallback(
    async (blob: Blob, durationSec: number) => {
      const tempId = `msg-${Date.now()}`;
      const now = new Date().toISOString();
      const ext = blob.type.includes('ogg')
        ? 'ogg' : blob.type.includes('mp4')
        ? 'mp4' :'webm';
      const fileName = `${localUserId ?? 'anon'}/${tempId}.${ext}`;

      const optimisticMsg: Message = {
        id: tempId,
        senderId: 'me',
        audioUrl: URL.createObjectURL(blob),
        audioDuration: durationSec,
        timestamp: now,
        status: 'sending',
        reactions: [],
        type: 'audio',
      };

      setMessages((prev) => [...prev, optimisticMsg]);

      if (localUserId && remoteUserId) {
        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('voice-messages')
          .upload(fileName, blob, { contentType: blob.type, upsert: false });

        if (uploadError) {
          // Fallback: keep local blob URL
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'sent' as const } : m))
          );
          return;
        }

        const { data: urlData } = supabase.storage
          .from('voice-messages')
          .getPublicUrl(uploadData.path);

        const publicUrl = urlData.publicUrl;

        const { data, error } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversationId,
            sender_id: localUserId,
            receiver_id: remoteUserId,
            audio_url: publicUrl,
            audio_duration: durationSec,
            msg_type: 'audio',
            msg_status: 'sent',
          })
          .select()
          .single();

        if (!error && data) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempId
                ? { ...rowToMessage(data as Record<string, unknown>), reactions: [] }
                : m
            )
          );
          setTimeout(async () => {
            await supabase
              .from('messages')
              .update({ msg_status: 'delivered' })
              .eq('id', (data as Record<string, unknown>).id as string);
          }, 800);
        } else {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, audioUrl: publicUrl, status: 'sent' as const } : m))
          );
        }
      } else {
        // Local simulation
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'delivered' as const } : m))
          );
        }, 800);
      }
    },
    [localUserId, remoteUserId, conversationId]
  );

  const sendFileMessage = useCallback(
    async (file: File) => {
      const tempId = `msg-${Date.now()}`;
      const now = new Date().toISOString();
      const isImage = file.type.startsWith('image/');
      const localUrl = URL.createObjectURL(file);

      const optimisticMsg: Message = {
        id: tempId,
        senderId: 'me',
        fileUrl: localUrl,
        fileName: file.name,
        fileSize: file.size,
        fileMime: file.type,
        timestamp: now,
        status: 'sending',
        reactions: [],
        type: 'file',
      };

      setMessages((prev) => [...prev, optimisticMsg]);

      if (localUserId && remoteUserId) {
        const ext = file.name.split('.').pop() ?? 'bin';
        const filePath = `${localUserId}/${tempId}.${ext}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('chat-files')
          .upload(filePath, file, { contentType: file.type, upsert: false });

        if (uploadError) {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'sent' as const } : m))
          );
          return;
        }

        const { data: urlData } = supabase.storage
          .from('chat-files')
          .getPublicUrl(uploadData.path);

        const publicUrl = urlData.publicUrl;

        const { data, error } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversationId,
            sender_id: localUserId,
            receiver_id: remoteUserId,
            file_url: publicUrl,
            file_name: file.name,
            file_size: file.size,
            file_mime: file.type,
            msg_type: 'file',
            msg_status: 'sent',
          })
          .select()
          .single();

        if (!error && data) {
          URL.revokeObjectURL(localUrl);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempId
                ? { ...rowToMessage(data as Record<string, unknown>), reactions: [] }
                : m
            )
          );
          setTimeout(async () => {
            await supabase
              .from('messages')
              .update({ msg_status: 'delivered' })
              .eq('id', (data as Record<string, unknown>).id as string);
          }, 800);
        } else {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, fileUrl: publicUrl, status: 'sent' as const } : m))
          );
        }
      } else {
        // Local simulation
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'delivered' as const } : m))
          );
        }, 800);
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: 'read' as const } : m))
          );
        }, 2000);
      }
    },
    [localUserId, remoteUserId, conversationId]
  );

  const saveEditedMessage = useCallback(async (msgId: string, newText: string) => {
    const editedAt = new Date().toISOString();
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId ? { ...m, text: newText, editedAt } : m
      )
    );
    setEditingMessageId(null);

    if (localUserId) {
      await supabase
        .from('messages')
        .update({ text: newText, edited_at: editedAt })
        .eq('id', msgId)
        .eq('sender_id', localUserId);
    }
  }, [localUserId]);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  const openContextMenu = useCallback((msgId: string, x: number, y: number) => {
    setContextMenu({ msgId, x, y });
    setReactionPicker(null);
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const openReactionPicker = useCallback((msgId: string, x: number, y: number) => {
    setReactionPicker({ msgId, x, y });
    setContextMenu(null);
  }, []);

  const closeReactionPicker = useCallback(() => setReactionPicker(null), []);

  const handleReaction = useCallback((msgId: string, emoji: string) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msgId) return m;
        const existing = m.reactions ?? [];
        const idx = existing.findIndex((r) => r.emoji === emoji);
        if (idx >= 0) {
          const updated = [...existing];
          if (updated[idx].byMe) {
            updated[idx] = { ...updated[idx], count: updated[idx].count - 1, byMe: false };
            if (updated[idx].count <= 0) updated.splice(idx, 1);
          } else {
            updated[idx] = { ...updated[idx], count: updated[idx].count + 1, byMe: true };
          }
          return { ...m, reactions: updated };
        }
        return { ...m, reactions: [...existing, { emoji, count: 1, byMe: true }] };
      })
    );
    setReactionPicker(null);
  }, []);

  const handleContextAction = useCallback(
    async (action: string, msgId: string) => {
      const msg = messages.find((m) => m.id === msgId);
      if (!msg) return;
      switch (action) {
        case 'reply':
          setReplyTo(msg);
          break;
        case 'react':
          if (contextMenu) openReactionPicker(msgId, contextMenu.x, contextMenu.y);
          break;
        case 'copy':
          if (msg.text) navigator.clipboard.writeText(msg.text).catch(() => {});
          break;
        case 'edit':
          setEditingMessageId(msgId);
          break;
        case 'pin':
          await handlePinMessage(msgId);
          break;
        case 'forward':
          setForwardShareModal({ message: msg, mode: 'forward' });
          break;
        case 'share':
          setForwardShareModal({ message: msg, mode: 'share' });
          break;
        case 'select':
          setIsSelecting(true);
          setSelectedIds(new Set([msgId]));
          break;
        case 'delete':
          setMessages((prev) => prev.filter((m) => m.id !== msgId));
          if (localUserId) {
            await supabase
              .from('messages')
              .delete()
              .eq('id', msgId)
              .eq('sender_id', localUserId);
          }
          break;
      }
      setContextMenu(null);
    },
    [messages, contextMenu, openReactionPicker, localUserId, handlePinMessage]
  );

  const toggleSelectMessage = useCallback((msgId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  }, []);

  const cancelSelection = useCallback(() => {
    setIsSelecting(false);
    setSelectedIds(new Set());
  }, []);

  const deleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    setMessages((prev) => prev.filter((m) => !selectedIds.has(m.id)));
    cancelSelection();
    if (localUserId && ids.length > 0) {
      await supabase
        .from('messages')
        .delete()
        .in('id', ids)
        .eq('sender_id', localUserId);
    }
  }, [selectedIds, cancelSelection, localUserId]);

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#101C29',
      }}
    >
      {isSelecting ? (
        <SelectionHeader
          count={selectedIds.size}
          onCancel={cancelSelection}
          onDelete={deleteSelected}
        />
      ) : (
        <ChatHeader
          isTyping={isTyping}
          onBack={onBack}
          onViewProfile={onViewProfile}
          onOpenSettings={onOpenSettings}
          onVoiceCall={() => setInVoiceCall(true)}
          onVideoCall={() => setInVideoCall(true)}
          onOpenGallery={() => setInGallery(true)}
          remoteUserName={remoteUserName}
          isBlocked={isBlocked}
          onBlockContact={handleBlockContact}
          onDeleteChat={handleDeleteChat}
        />
      )}

      {pinnedMessage && !isSelecting && (
        <PinnedMessageBanner
          message={pinnedMessage}
          onUnpin={handleUnpinMessage}
          onScrollToMessage={scrollToPinnedMessage}
        />
      )}

      {inGallery && (
        <MediaGalleryScreen
          chatId={chatId}
          localUserId={localUserId}
          remoteUserId={remoteUserId}
          remoteUserName={remoteUserName}
          messages={messages}
          onBack={() => setInGallery(false)}
        />
      )}

      <ChatMessageArea
        messages={messages}
        isSelecting={isSelecting}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelectMessage}
        onContextMenu={openContextMenu}
        onReply={setReplyTo}
        isTyping={isTyping}
        replyTo={replyTo}
        editingMessageId={editingMessageId}
        onSaveEdit={saveEditedMessage}
        onCancelEdit={cancelEdit}
      />

      {/* Red tint overlay when contact is blocked */}
      {isBlocked && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background: 'rgba(220, 38, 38, 0.08)',
            top: '64px', // below header
          }}
        />
      )}

      {isBlocked ? (
        <div
          className="flex-shrink-0 safe-bottom flex flex-col items-center justify-center gap-3 px-6 py-5"
          style={{
            background: 'rgba(16,28,41,0.97)',
            borderTop: '1px solid rgba(220,38,38,0.25)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          <p className="text-[13.5px] font-medium text-center" style={{ color: 'rgba(220,38,38,0.85)' }}>
            You have blocked this contact. You can&apos;t send or receive messages.
          </p>
          <button
            onClick={handleBlockContact}
            className="px-6 py-2.5 rounded-full text-[14px] font-semibold transition-all duration-150 active:scale-95"
            style={{
              background: 'rgba(220,38,38,0.15)',
              border: '1px solid rgba(220,38,38,0.4)',
              color: '#ef4444',
            }}
          >
            Unblock Contact
          </button>
        </div>
      ) : (
        <MessageComposer
          chatId={chatId}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSend={sendMessage}
          onSendVoice={sendVoiceMessage}
          onSendFile={sendFileMessage}
        />
      )}

      {contextMenu && (
        <ContextMenu
          state={contextMenu}
          messageId={contextMenu.msgId}
          isMine={messages.find((m) => m.id === contextMenu.msgId)?.senderId === 'me'}
          onAction={handleContextAction}
          onClose={closeContextMenu}
        />
      )}

      {reactionPicker && (
        <ReactionPicker
          state={reactionPicker}
          onReact={(emoji) => handleReaction(reactionPicker.msgId, emoji)}
          onClose={closeReactionPicker}
        />
      )}

      {forwardShareModal && (
        <ForwardShareModal
          message={forwardShareModal.message}
          mode={forwardShareModal.mode}
          onClose={() => setForwardShareModal(null)}
        />
      )}

      {inVideoCall && (
        <VideoCallScreen
          chatId={chatId ?? 'default'}
          localUserId={localUserId ?? 'user-local'}
          remoteUserId={remoteUserId ?? 'user-remote'}
          remoteUserName={remoteUserName ?? 'Ate Boni'}
          onEnd={() => setInVideoCall(false)}
        />
      )}

      {inVoiceCall && (
        <VoiceCallScreen
          chatId={chatId ?? 'default'}
          localUserId={localUserId ?? 'user-local'}
          remoteUserId={remoteUserId ?? 'user-remote'}
          remoteUserName={remoteUserName ?? 'Ate Boni'}
          onEnd={() => setInVoiceCall(false)}
        />
      )}
    </div>
  );
}